/**
 * Image URL Helper - DUAL-READ STRATEGY
 * 
 * Priority order for image resolution:
 * 1. cloudinary_image_url (shadow field from migration)
 * 2. cloudinary_images[] (shadow array for galleries)
 * 3. legacy_image_url (backup of original)
 * 4. original field (existing /uploads path)
 * 5. placeholder image
 * 
 * This ensures migrated Cloudinary images are used while
 * maintaining full backwards compatibility with legacy URLs.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Old domains that need to be converted to current backend URL
const OLD_DOMAINS = [
  'https://cors-fix-live-2.preview.emergentagent.com',
  'http://url-path-repair.preview.emergentagent.com'
];

// Cloudinary domain - these URLs are already complete
const CLOUDINARY_DOMAIN = 'res.cloudinary.com';

/**
 * Add Cloudinary auto-optimization transformations to a URL
 * @param {string} url - Cloudinary URL
 * @param {object} options - Optimization options
 * @returns {string} - Optimized URL
 */
const addCloudinaryOptimizations = (url, options = {}) => {
  if (!url || !url.includes(CLOUDINARY_DOMAIN)) return url;
  
  const { width, quality = 'auto', format = 'auto' } = options;
  
  // Build transformation string
  let transforms = `f_${format},q_${quality}`;
  if (width) {
    transforms += `,w_${width},c_limit`;
  }
  
  // Insert transformations after /upload/
  if (url.includes('/upload/')) {
    const parts = url.split('/upload/');
    if (parts.length === 2) {
      const afterUpload = parts[1];
      // Check if there's already a transformation
      if (afterUpload.match(/^[a-z]_/)) {
        // Already has transformations, add ours at the beginning
        return `${parts[0]}/upload/${transforms},${afterUpload}`;
      } else {
        // No transformations yet, add ours
        return `${parts[0]}/upload/${transforms}/${afterUpload}`;
      }
    }
  }
  
  return url;
};

/**
 * Resolve the best available image URL from a source object
 * Implements the DUAL-READ STRATEGY with Cloudinary priority
 * 
 * @param {object|string} source - Source object with image fields, or string URL
 * @param {string} fieldName - The original field name ('image', 'images', etc.)
 * @param {number} index - For array fields, the index to retrieve
 * @returns {string} - The best available image URL
 */
export const resolveImageUrl = (source, fieldName = 'image', index = null) => {
  // If source is a string, treat it as a direct URL
  if (typeof source === 'string') {
    return source;
  }
  
  // If source is null/undefined
  if (!source) return '';
  
  // DUAL-READ STRATEGY - Priority order:
  
  // For single image fields (e.g., 'image')
  if (index === null) {
    // 1. Check cloudinary shadow field
    const cloudinaryField = `cloudinary_${fieldName}_url`;
    if (source[cloudinaryField] && source[cloudinaryField].includes(CLOUDINARY_DOMAIN)) {
      return source[cloudinaryField];
    }
    
    // 2. Check generic cloudinary field (for CMS)
    const cloudinaryGeneric = `cloudinary_${fieldName}`;
    if (source[cloudinaryGeneric] && source[cloudinaryGeneric].includes(CLOUDINARY_DOMAIN)) {
      return source[cloudinaryGeneric];
    }
    
    // 3. Check legacy backup field
    const legacyField = `legacy_${fieldName}_url`;
    if (source[legacyField]) {
      return source[legacyField];
    }
    
    // 4. Fall back to original field
    if (source[fieldName]) {
      return source[fieldName];
    }
    
    return '';
  }
  
  // For array fields (e.g., 'images[0]')
  const cloudinaryArrayField = `cloudinary_${fieldName}`;
  const originalArray = source[fieldName] || [];
  const cloudinaryArray = source[cloudinaryArrayField] || [];
  
  // 1. Check cloudinary array at index
  if (cloudinaryArray[index] && cloudinaryArray[index].includes(CLOUDINARY_DOMAIN)) {
    return cloudinaryArray[index];
  }
  
  // 2. Fall back to original array at index
  if (originalArray[index]) {
    return originalArray[index];
  }
  
  return '';
};

/**
 * Get the full image URL, handling Cloudinary, legacy URLs, and local paths
 * 
 * @param {string|object} urlOrSource - The image URL string, OR a source object
 * @param {object} options - Optional: { width, quality, format, field, index }
 * @returns {string} - The full image URL
 */
export const getImageUrl = (urlOrSource, options = {}) => {
  // Handle source object with dual-read strategy
  if (typeof urlOrSource === 'object' && urlOrSource !== null) {
    const { field = 'image', index = null, ...cloudinaryOptions } = options;
    const resolvedUrl = resolveImageUrl(urlOrSource, field, index);
    return getImageUrl(resolvedUrl, cloudinaryOptions);
  }
  
  const url = urlOrSource;
  if (!url) return '';
  
  // Cloudinary URLs - add auto optimization and return
  if (url.includes(CLOUDINARY_DOMAIN)) {
    return addCloudinaryOptimizations(url, options);
  }
  
  // Check for old domains and convert to current backend URL
  for (const oldDomain of OLD_DOMAINS) {
    if (url.startsWith(oldDomain)) {
      const relativePath = url.replace(oldDomain, '');
      return `${BACKEND_URL}${relativePath}`;
    }
  }
  
  // If already a full URL (not an old domain), return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If local/relative path starting with /uploads/ or /api/uploads/
  if (url.startsWith('/uploads/') || url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If local/relative path, prepend backend URL
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  return url;
};

/**
 * Get the best image URL for a product (main image)
 * Uses dual-read strategy: Cloudinary first, then legacy
 * 
 * @param {object} product - Product object with image fields
 * @param {object} options - Cloudinary optimization options
 * @returns {string} - The best available image URL
 */
export const getProductImageUrl = (product, options = {}) => {
  if (!product) return '';
  
  // Priority: cloudinary_image_url > legacy_image_url > image
  const url = product.cloudinary_image_url 
    || product.legacy_image_url 
    || product.image 
    || '';
  
  return getImageUrl(url, options);
};

/**
 * Get the best gallery images for a product
 * Uses dual-read strategy: cloudinary_images[] first, then images[]
 * 
 * @param {object} product - Product object with image fields
 * @param {object} options - Cloudinary optimization options
 * @returns {string[]} - Array of best available image URLs
 */
export const getProductGalleryUrls = (product, options = {}) => {
  if (!product) return [];
  
  const originalImages = product.images || [];
  const cloudinaryImages = product.cloudinary_images || [];
  
  // Merge arrays: prefer cloudinary when available
  const result = [];
  const maxLength = Math.max(originalImages.length, cloudinaryImages.length);
  
  for (let i = 0; i < maxLength; i++) {
    // Prefer cloudinary URL if it exists and is valid
    if (cloudinaryImages[i] && cloudinaryImages[i].includes(CLOUDINARY_DOMAIN)) {
      result.push(getImageUrl(cloudinaryImages[i], options));
    } else if (originalImages[i]) {
      result.push(getImageUrl(originalImages[i], options));
    }
  }
  
  return result;
};

/**
 * Get optimized image URL with specific width (for responsive images)
 * @param {string} url - The image URL
 * @param {number} width - Desired width
 * @returns {string} - Optimized URL
 */
export const getOptimizedImageUrl = (url, width) => {
  return getImageUrl(url, { width, quality: 'auto', format: 'auto' });
};

/**
 * Check if a URL is a Cloudinary URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if Cloudinary URL
 */
export const isCloudinaryUrl = (url) => {
  if (!url) return false;
  return url.includes(CLOUDINARY_DOMAIN);
};

/**
 * Generate Cloudinary srcset with responsive widths
 * Uses: f_auto, q_auto, dpr_auto, c_limit, w_XXX
 * 
 * @param {string} url - Cloudinary URL
 * @param {number[]} widths - Array of widths
 * @returns {string|null} - srcset string or null if not Cloudinary/parsing fails
 */
export const generateCloudinarySrcSet = (url, widths = [320, 480, 640, 800, 960]) => {
  if (!url || !url.includes(CLOUDINARY_DOMAIN)) return null;
  
  // Safety: check for /upload/ segment
  if (!url.includes('/upload/')) return null;
  
  try {
    const parts = url.split('/upload/');
    if (parts.length !== 2) return null;
    
    // Remove any existing transformations (starts with letter_)
    let imagePath = parts[1];
    if (imagePath.match(/^[a-z]_/)) {
      // Has existing transforms, find the version marker (v followed by numbers)
      const versionMatch = imagePath.match(/(v\d+\/.*)/);
      if (versionMatch) {
        imagePath = versionMatch[1];
      }
    }
    
    const srcset = widths.map(w => {
      const transforms = `f_auto,q_auto,dpr_auto,c_limit,w_${w}`;
      return `${parts[0]}/upload/${transforms}/${imagePath} ${w}w`;
    }).join(', ');
    
    return srcset;
  } catch (e) {
    // Parsing failed - return null (fallback to original)
    return null;
  }
};

/**
 * Get single optimized Cloudinary URL (for src fallback)
 * @param {string} url - Cloudinary URL
 * @param {number} width - Target width (default 800)
 * @returns {string} - Optimized URL or original if parsing fails
 */
export const getCloudinaryUrl = (url, width = 800) => {
  if (!url || !url.includes(CLOUDINARY_DOMAIN)) return url;
  if (!url.includes('/upload/')) return url;
  
  try {
    const parts = url.split('/upload/');
    if (parts.length !== 2) return url;
    
    // Remove any existing transformations
    let imagePath = parts[1];
    if (imagePath.match(/^[a-z]_/)) {
      const versionMatch = imagePath.match(/(v\d+\/.*)/);
      if (versionMatch) {
        imagePath = versionMatch[1];
      }
    }
    
    const transforms = `f_auto,q_auto,dpr_auto,c_limit,w_${width}`;
    return `${parts[0]}/upload/${transforms}/${imagePath}`;
  } catch (e) {
    return url;
  }
};

/**
 * Check if a URL is from an old/legacy domain
 * @param {string} url - The URL to check
 * @returns {boolean} - True if from an old domain
 */
export const isLegacyUrl = (url) => {
  if (!url) return false;
  return OLD_DOMAINS.some(domain => url.startsWith(domain));
};

/**
 * Check if a product has been migrated to Cloudinary
 * @param {object} product - Product object
 * @returns {boolean} - True if migrated
 */
export const isProductMigrated = (product) => {
  if (!product) return false;
  return !!product.cloudinary_image_url || 
         (product.cloudinary_images && product.cloudinary_images.length > 0);
};

export default getImageUrl;
